import React from "react";
import { Button, Tooltip } from "antd";
import { DeleteOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { MODAL_BUTTON_KEYS } from "constants/addOption";
import {
  ADD_TENOR_EXCEL_COLUMNS,
  ADD_TENOR_EXCEL_ROW_GUTTER_WIDTH,
  ADD_TENOR_TABLE_COLUMNS,
  ADD_TENOR_TABLE_HEADER_TOOLTIPS,
  ADD_TENOR_TABLE_LABELS,
  addTenorExcelColWidthPercent,
} from "constants/addTenorTable";
import {
  canRemoveAddTenorBlock,
  isAddTenorContinuationRow,
  isAddTenorExistingCatalogTenorRow,
} from "helpers/Pricer/addTenorRowExpansion";

function AddTenorExcelGrid({
  rows,
  rowErrorsByKey,
  lockPcs,
  renderCell,
  onRemoveRow,
  removeRowDisabled = false,
}) {
  return (
    <div className="add-tenor-excel-scroll">
      <table className="add-tenor-excel-table" cellSpacing={0} cellPadding={0}>
        <colgroup>
          <col style={{ width: addTenorExcelColWidthPercent(ADD_TENOR_EXCEL_ROW_GUTTER_WIDTH) }} />
          {ADD_TENOR_EXCEL_COLUMNS.map((col) => (
            <col key={col.key} style={{ width: addTenorExcelColWidthPercent(col.width) }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th className="add-tenor-excel-th add-tenor-excel-th--corner" scope="col" />
            {ADD_TENOR_EXCEL_COLUMNS.map((col) => {
              const tooltip = ADD_TENOR_TABLE_HEADER_TOOLTIPS[col.key];
              return (
                <th key={col.key} className="add-tenor-excel-th" scope="col">
                  {ADD_TENOR_TABLE_LABELS[col.key]}
                  {tooltip ? (
                    <Tooltip title={tooltip}>
                      <InfoCircleOutlined className="add-tenor-excel-th__info" />
                    </Tooltip>
                  ) : null}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const errors = rowErrorsByKey[row.key];
            const isContinuation = isAddTenorContinuationRow(row);
            const isExistingUpdate = isAddTenorExistingCatalogTenorRow(row, rows);
            const monthTableColumns = [
              ADD_TENOR_TABLE_COLUMNS.SUB_LEG,
              ADD_TENOR_TABLE_COLUMNS.START_DATE,
              ADD_TENOR_TABLE_COLUMNS.END_DATE,
            ];

            return (
              <tr
                key={row.key}
                className={[
                  isExistingUpdate ? "add-tenor-excel-row--existing-update" : "",
                  isContinuation ? "add-tenor-excel-row--multisub" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <td className="add-tenor-excel-td add-tenor-excel-td--rownum">
                  <div className="add-tenor-excel-rownum">
                    {canRemoveAddTenorBlock(rows, row.key) ? (
                      <Tooltip title={MODAL_BUTTON_KEYS.REMOVE_ROW}>
                        <Button
                          type="text"
                          size="small"
                          className="add-tenor-excel-rownum__remove"
                          icon={<DeleteOutlined />}
                          disabled={removeRowDisabled}
                          aria-label={MODAL_BUTTON_KEYS.REMOVE_ROW}
                          onClick={() => onRemoveRow?.(row.key)}
                        />
                      </Tooltip>
                    ) : null}
                    <span className="add-tenor-excel-rownum__index">
                      {index + 1}
                    </span>
                  </div>
                </td>
                {ADD_TENOR_EXCEL_COLUMNS.map((col) => {
                  const editableOnContinuation = [
                    ADD_TENOR_TABLE_COLUMNS.START_DATE,
                    ADD_TENOR_TABLE_COLUMNS.END_DATE,
                  ].includes(col.key);
                  const continuationAlignColumns = [
                    ADD_TENOR_TABLE_COLUMNS.PRODUCT,
                    ADD_TENOR_TABLE_COLUMNS.CONTRACT,
                    ADD_TENOR_TABLE_COLUMNS.STYLE,
                    ADD_TENOR_TABLE_COLUMNS.PERIOD,
                    ADD_TENOR_TABLE_COLUMNS.LINK,
                    ADD_TENOR_TABLE_COLUMNS.TENOR,
                    ADD_TENOR_TABLE_COLUMNS.UL_TENOR,
                  ];
                  const isContinuationField =
                    isContinuation &&
                    !editableOnContinuation &&
                    continuationAlignColumns.includes(col.key);
                  const isLocked = lockPcs && col.pcsLocked;

                  return (
                    <td
                      key={col.key}
                      className={[
                        "add-tenor-excel-td",
                        isLocked
                          ? "add-tenor-excel-td--locked"
                          : "add-tenor-excel-td--edit",
                        isContinuationField
                          ? "add-tenor-excel-td--continuation-field"
                          : "",
                        isExistingUpdate && monthTableColumns.includes(col.key)
                          ? "add-tenor-excel-td--existing-month"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      {renderCell(col.key, row, errors)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default AddTenorExcelGrid;
